import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { CalendarIcon, Clock, LayoutTemplate, Plus, Trash2 } from "lucide-react";
import { cn } from "@/lib/utils";
import { format, addDays, setHours, setMinutes } from "date-fns";
import { ptBR } from "date-fns/locale";
import { supabase } from "@/integrations/supabase/client";

export interface DraftScheduleMessage {
  body: string;
  date: Date | undefined;
  time: string;
  sourceKind: "manual" | "from_history";
  sourceMessageId: string | null;
}

export const MAX_SCHEDULE_MESSAGES = 5;

export const emptyScheduleMessage = (): DraftScheduleMessage => ({
  body: "",
  date: undefined,
  time: "10:00",
  sourceKind: "manual",
  sourceMessageId: null,
});

export function buildSchedulePayload(messages: DraftScheduleMessage[]) {
  const now = new Date();
  const payload: {
    body: string;
    scheduled_at: string;
    source_kind: string;
    source_message_id: string | null;
  }[] = [];

  for (const m of messages) {
    if (!m.date || !m.body.trim()) {
      throw new Error("Preencha data e conteúdo de todas as mensagens.");
    }
    const [hours, minutes] = m.time.split(":").map(Number);
    const scheduledAt = setMinutes(setHours(m.date, hours), minutes);
    if (scheduledAt <= now) {
      throw new Error("Todos os agendamentos devem ser no futuro.");
    }
    payload.push({
      body: m.body.trim(),
      scheduled_at: scheduledAt.toISOString(),
      source_kind: m.sourceKind,
      source_message_id: m.sourceMessageId,
    });
  }

  return payload;
}

interface ScheduleMessagesFormProps {
  workspaceId: string;
  messages: DraftScheduleMessage[];
  onMessagesChange: (messages: DraftScheduleMessage[]) => void;
  submitLabel: string;
  onSubmit: () => void | Promise<void>;
  onBack?: () => void;
  isSubmitting?: boolean;
}

export function ScheduleMessagesForm({
  workspaceId,
  messages,
  onMessagesChange,
  submitLabel,
  onSubmit,
  onBack,
  isSubmitting = false,
}: ScheduleMessagesFormProps) {
  const [templates, setTemplates] = useState<
    { id: string; title: string; content: string }[]
  >([]);
  const [templatesForIndex, setTemplatesForIndex] = useState<number | null>(null);

  const updateMessage = (idx: number, patch: Partial<DraftScheduleMessage>) => {
    onMessagesChange(
      messages.map((m, i) => (i === idx ? { ...m, ...patch } : m)),
    );
  };

  const addMessage = () => {
    if (messages.length >= MAX_SCHEDULE_MESSAGES) return;
    onMessagesChange([...messages, emptyScheduleMessage()]);
  };

  const removeMessage = (idx: number) => {
    if (messages.length === 1) return;
    onMessagesChange(messages.filter((_, i) => i !== idx));
  };

  const handleSetShortcut = (idx: number, days: number) => {
    updateMessage(idx, { date: addDays(new Date(), days) });
  };

  const loadTemplates = async (idx: number) => {
    if (templates.length === 0) {
      const { data } = await supabase
        .from("workspace_layouts")
        .select("id, title, content")
        .eq("workspace_id", workspaceId)
        .eq("type", "text")
        .order("created_at", { ascending: false });
      if (data) {
        setTemplates(data.filter((t) => t.content && t.content.trim().length > 0));
      }
    }
    setTemplatesForIndex(idx);
  };

  const selectTemplate = (idx: number, tpl: { id: string; content: string }) => {
    updateMessage(idx, {
      body: tpl.content,
      sourceKind: "from_history",
      sourceMessageId: tpl.id,
    });
    setTemplatesForIndex(null);
  };

  const allValid = messages.every((m) => m.date && m.body.trim().length > 0);

  return (
    <div className="space-y-4 py-2">
      {messages.map((m, idx) => (
        <div key={idx} className="rounded-lg border p-3 space-y-3 bg-muted/20">
          <div className="flex items-center justify-between">
            <p className="text-sm font-medium">Mensagem {idx + 1}</p>
            {messages.length > 1 && (
              <Button
                variant="ghost"
                size="sm"
                className="h-7 w-7 p-0 text-muted-foreground hover:text-destructive"
                onClick={() => removeMessage(idx)}
                aria-label="Remover mensagem"
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            )}
          </div>

          <div className="space-y-2">
            <Label className="text-xs">Data de envio</Label>
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  className={cn(
                    "w-full justify-start text-left font-normal",
                    !m.date && "text-muted-foreground",
                  )}
                >
                  <CalendarIcon className="mr-2 h-4 w-4" />
                  {m.date
                    ? format(m.date, "dd/MM/yyyy", { locale: ptBR })
                    : "Selecione a data"}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start">
                <Calendar
                  mode="single"
                  selected={m.date}
                  onSelect={(d) => updateMessage(idx, { date: d })}
                  disabled={(d) => d < new Date(new Date().setHours(0, 0, 0, 0))}
                  locale={ptBR}
                  className={cn("p-3 pointer-events-auto")}
                />
              </PopoverContent>
            </Popover>

            <div className="flex gap-2 flex-wrap">
              {[
                { label: "+1 dia", days: 1 },
                { label: "+7 dias", days: 7 },
                { label: "+15 dias", days: 15 },
                { label: "+30 dias", days: 30 },
              ].map((s) => (
                <Badge
                  key={s.days}
                  variant="secondary"
                  className="cursor-pointer hover:bg-primary/20 transition-colors"
                  onClick={() => handleSetShortcut(idx, s.days)}
                >
                  {s.label}
                </Badge>
              ))}
            </div>
          </div>

          <div className="space-y-2">
            <Label className="text-xs">Horário</Label>
            <div className="flex items-center gap-2">
              <Clock className="h-4 w-4 text-muted-foreground" />
              <Input
                type="time"
                value={m.time}
                onChange={(e) => updateMessage(idx, { time: e.target.value })}
                className="w-32"
              />
            </div>
          </div>

          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label className="text-xs">Mensagem</Label>
              <Button
                variant="ghost"
                size="sm"
                className="h-7 text-xs"
                onClick={() => loadTemplates(idx)}
              >
                <LayoutTemplate className="w-3 h-3 mr-1" />
                Usar template
              </Button>
            </div>
            <Textarea
              value={m.body}
              onChange={(e) => {
                updateMessage(idx, {
                  body: e.target.value,
                  ...(m.sourceKind === "from_history"
                    ? { sourceKind: "manual", sourceMessageId: null }
                    : {}),
                });
              }}
              placeholder="Digite a mensagem que será enviada..."
              rows={3}
            />
            {m.sourceKind === "from_history" && (
              <p className="text-xs text-muted-foreground">Copiada de um template</p>
            )}
          </div>

          {templatesForIndex === idx && templates.length > 0 && (
            <div className="border rounded-md max-h-40 overflow-y-auto divide-y bg-background">
              {templates.map((tpl) => (
                <button
                  key={tpl.id}
                  type="button"
                  className="w-full text-left px-3 py-2 hover:bg-muted/50 transition-colors text-sm"
                  onClick={() => selectTemplate(idx, tpl)}
                >
                  <p className="font-medium text-xs">{tpl.title}</p>
                  <p className="line-clamp-2 text-muted-foreground">{tpl.content}</p>
                </button>
              ))}
            </div>
          )}
          {templatesForIndex === idx && templates.length === 0 && (
            <p className="text-sm text-muted-foreground text-center py-3">
              Nenhum template de texto encontrado.
            </p>
          )}
        </div>
      ))}

      {messages.length < MAX_SCHEDULE_MESSAGES && (
        <Button variant="outline" className="w-full" onClick={addMessage}>
          <Plus className="w-4 h-4 mr-2" />
          Adicionar nova mensagem ({messages.length}/{MAX_SCHEDULE_MESSAGES})
        </Button>
      )}

      <div className="flex gap-2 justify-end">
        {onBack && (
          <Button variant="ghost" onClick={onBack} disabled={isSubmitting}>
            Voltar
          </Button>
        )}
        <Button onClick={() => void onSubmit()} disabled={isSubmitting || !allValid}>
          {isSubmitting ? "Agendando..." : submitLabel}
        </Button>
      </div>
    </div>
  );
}
